import React, { useCallback, useState } from 'react';
import { DatePicker } from 'web-library';
import { useTranslation } from 'react-i18next';

import ToggleDisplayEditFormItem from '../../../../../components/ToggleDisplayEditFormItem';
import { CandidateProfilePersonalParticularsProps } from '../CandidateProfilePersonalParticulars';
import { useStyles } from '../styles';
import ControlledAutocomplete from '../../../../../components/form-items/ControlledAutocomplete';
import {
  DROPDOWN_PLACEHOLDER,
  getIExamDropdownItemLabel,
  getIExamMasterCodeSelected,
} from '../../../logic/useCandidateProfileForm';
import { useGetIExamMasterCode } from '../../../../../api-refactored/user/useGetIExamMasterCode';
import { UsePrCitizenshipFormReturn } from '../../../../accountManagement/logic/usePrCitizenshipForm';
import { Dayjs } from 'dayjs';
import {IExamMasterCodeItem} from "../../../../../api/user/model";
import {dayjsToSemanticDisplay} from "../../../../../utils/DateUtils";
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

const CandidateNationalityRelatedComponent = ({
  isEditState,
  formHook,
  isDisableInputs = undefined,
  isHideExtraFormInfo = false,
  formDefaultValues = undefined,
  prCitizenHook = undefined,
}: CandidateProfilePersonalParticularsProps & { prCitizenHook?: UsePrCitizenshipFormReturn }) => {
  const { register, formState:{errors}, control, setValue } = formHook;
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [nationalityIssueDate, setNationalityIssueDate] = useState<string | undefined>(
    formDefaultValues?.nationalityIssueDate
  );

  const prStatusLabel = t('profile->>CreateCandidateForm.PR Status');
  const citizenshipLabel = t('profile->>CreateCandidateForm.CitizenshipType');

  const {
    data: dataIEXAM,
    isLoading: isLoadingIEXAM,
    error: errorMasterCode,
    refetch: refetchMasterCode,
  } = useGetIExamMasterCode();

  const nationalityDropdownOption = dataIEXAM ? dataIEXAM.nationality : [];
  const citizenshipDropdownOption = dataIEXAM ? dataIEXAM.citizenship : [];

  const handleNationalityIssueDateOnChange = useCallback(
    (date: string | undefined) => {
      setNationalityIssueDate(date || '');
      setValue('nationalityIssueDate', date || '');
    },
    [setValue]
  );

  return (
    <>
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('profile->>CreateCandidateForm.Nationality')}
        valueText={formDefaultValues?.nationality?.codeValueDescription}
        containerClassName={`${classes.formElementContainer}`}
        EditComponent={
          <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
            label={t('profile->>CreateCandidateForm.Nationality')}
            control={control}
            name="nationality"
            id="input-select-nationality"
            options={nationalityDropdownOption}
            getOptionLabel={getIExamDropdownItemLabel}
            error={!!errors.nationality}
            helperText={errors?.nationality?.message?.toString()}
            loading={isLoadingIEXAM}
            loadingError={!!errorMasterCode}
            loadingErrorOnReFetch={refetchMasterCode}
            placeholder={DROPDOWN_PLACEHOLDER}
            disabled={isDisableInputs?.nationality}
            isOptionEqualToValue={getIExamMasterCodeSelected}
            isRequired
          />
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('profile->>CreateCandidateForm.Nationality Issue Date')}
        allHide
        valueText={formDefaultValues?.nationalityIssueDate}
        containerClassName={`${classes.formElementContainer} ${classes.halfWidthRightItem}`}
        EditComponent={
          formHook && <ControlledDatePicker
            label={t('profile->>CreateCandidateForm.NationalityIssueDate')}
            value={nationalityIssueDate}
            onChange={handleNationalityIssueDateOnChange}
            inputVariant="filled"
            name="nationalityIssueDate"
            control={control}
            error={!!errors.nationalityIssueDate}
            helperText={errors?.nationalityIssueDate?.message?.toString()}
            disableFuture
          />
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={prStatusLabel}
        valueText={formDefaultValues?.prStatus?.codeValueDescription}
        containerClassName={`${classes.formElementContainer} ${
          isEditState ? '' : classes.halfWidthLeftItem
        }`}
        EditComponent={
          isDisableInputs ? (
            <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
              label={prStatusLabel}
              control={control}
              name="prStatus"
              id="input-select-prStatus"
              error={!!errors?.prStatus}
              helperText={errors?.prStatus?.message?.toString()}
              loading={isLoadingIEXAM}
              loadingError={!!errorMasterCode}
              loadingErrorOnReFetch={refetchMasterCode}
              options={prCitizenHook?.prStatusOption || []}
              getOptionLabel={getIExamDropdownItemLabel}
              placeholder={DROPDOWN_PLACEHOLDER}
              disabled={isDisableInputs?.prStatus || prCitizenHook?.isForeignerSetting}
              isOptionEqualToValue={getIExamMasterCodeSelected}
              value={prCitizenHook?.prStatusState}
              onChange={prCitizenHook?.onPrStatusChange}
              isRequired
            />
          ) : (
            <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
              label={prStatusLabel}
              control={control}
              name="prStatus"
              id="input-select-prStatus"
              error={!!errors?.prStatus}
              helperText={errors?.prStatus?.message?.toString()}
              loading={isLoadingIEXAM}
              loadingError={!!errorMasterCode}
              loadingErrorOnReFetch={refetchMasterCode}
              options={prCitizenHook?.prStatusOption || []}
              getOptionLabel={getIExamDropdownItemLabel}
              placeholder={DROPDOWN_PLACEHOLDER}
              disabled={prCitizenHook?.isForeignerSetting}
              isOptionEqualToValue={getIExamMasterCodeSelected}
              isRequired
            />
          )
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={citizenshipLabel}
        valueText={formDefaultValues?.citizenship?.codeValueDescription}
        containerClassName={`${classes.formElementContainer}`}
        EditComponent={
          isDisableInputs ? (
            <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
              label={citizenshipLabel}
              control={control}
              name="citizenship"
              id="input-select-citizenship"
              error={!!errors.citizenship}
              value={prCitizenHook?.citizenshipState}
              onChange={(_, newValue) => {
                prCitizenHook?.setCitizenship(newValue);
              }}
              helperText={errors?.citizenship?.message?.toString()}
              loading={isLoadingIEXAM}
              loadingError={!!errorMasterCode}
              loadingErrorOnReFetch={refetchMasterCode}
              options={citizenshipDropdownOption}
              getOptionLabel={getIExamDropdownItemLabel}
              placeholder={DROPDOWN_PLACEHOLDER}
              disabled={isDisableInputs?.citizenship}
              isOptionEqualToValue={getIExamMasterCodeSelected}
              isRequired
            />
          ) : (
            <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
              label={citizenshipLabel}
              control={control}
              name="citizenship"
              id="input-select-citizenship"
              error={!!errors.citizenship}
              helperText={errors?.citizenship?.message?.toString()}
              loading={isLoadingIEXAM}
              loadingError={!!errorMasterCode}
              loadingErrorOnReFetch={refetchMasterCode}
              options={citizenshipDropdownOption}
              getOptionLabel={getIExamDropdownItemLabel}
              placeholder={DROPDOWN_PLACEHOLDER}
              isOptionEqualToValue={getIExamMasterCodeSelected}
              isRequired
            />
          )
        }
      />
    </>
  );
};

export default CandidateNationalityRelatedComponent;
